<script lang="typescript">
    import { state, setState } from "./stores.js";
    import { MessageType, ContactLensUseType } from "./types";
    import {
        getLensDetails,
        getBaseLensImage,
        getBaseLensName,
        getCurrency,
        slugify,
    } from "./utils";
    import { fly } from "svelte/transition";
    import { onDestroy } from "svelte";
    import {
        getContactLensCodes,
        contactLensOptions,
        contactLensBenefits,
    } from "./contactLensData.js";
    import { push } from "svelte-spa-router";
    import ContactLensCard from "./ContactLensCard.svelte";
    import ContactLensBenefitColumn from "./ContactLensBenefitColumn.svelte";
    import Carousel from "svelte-carousel";

    // redirect to / if we haven't picked options yet
    if (
        $state.contactLensType === null ||
        $state.contactLensPrescriptionType === null ||
        $state.contactLensWearDaysPerWeek === null ||
        $state.contactLensWearHoursPerDay === null ||
        $state.contactLensUseType === null
    )
        window.location.replace("/");

    const lensCardHeightClass = "h-56";
    let [lensCodes, recommended] = getContactLensCodes(
        $state.contactLensType,
        $state.contactLensPrescriptionType,
        $state.contactLensWearDaysPerWeek,
        $state.contactLensWearHoursPerDay,
        $state.contactLensUseType
    );

    // we might not have any contact lenses to show
    if (lensCodes == null) {
        lensCodes = [];
        window.location.replace("#/not-found/");
    }

    let alternateLensCodes = [];
    // alternatives may be available
    switch ($state.contactLensUseType) {
        case ContactLensUseType.DAILY:
            [alternateLensCodes] = getContactLensCodes(
                $state.contactLensType,
                $state.contactLensPrescriptionType,
                $state.contactLensWearDaysPerWeek,
                $state.contactLensWearHoursPerDay,
                ContactLensUseType.REUSABLE
            );
            break;

        case ContactLensUseType.REUSABLE:
            [alternateLensCodes] = getContactLensCodes(
                $state.contactLensType,
                $state.contactLensPrescriptionType,
                $state.contactLensWearDaysPerWeek,
                $state.contactLensWearHoursPerDay,
                ContactLensUseType.DAILY
            );
            break;
    }
    onDestroy(() => window.scrollTo(0, 0));
    let carousel;
    const handleNextLens = () => {
        carousel.goToNext();
    };

    const selectContactLensUseType = (contactLensUseType: ContactLensUseType) => {
        setState({
            message: MessageType.SET_CONTACT_LENS_USE_TYPE,
            contactLensUseType,
        });
    };
    const switchUseType = () => {
        const otherUseType: ContactLensUseType =
            $state.contactLensUseType === ContactLensUseType.REUSABLE
                ? ContactLensUseType.DAILY
                : ContactLensUseType.REUSABLE;
        selectContactLensUseType(otherUseType);
        [lensCodes, recommended] = getContactLensCodes(
            $state.contactLensType,
            $state.contactLensPrescriptionType,
            $state.contactLensWearDaysPerWeek,
            $state.contactLensWearHoursPerDay,
            $state.contactLensUseType
        );
    };

    const isRecommended = (lensCodes, recommended, index) => {
        return lensCodes[index] == recommended;
    };

    const detailURL = (lensCodes, index) => {
        return "/contact-lenses/details/" + slugify(lensCodes[index]) + "/";
    };

    // update slider slides based on screen size
    let screenWidth = window.innerWidth;
    let particlesToShow = 3;

    if (screenWidth < 800) {
        particlesToShow = 1;
    } else if (screenWidth < 1200) {
        particlesToShow = 2;
    }

    const getInitialPage = (lensCodes, recommended) => {
        let page = lensCodes.indexOf(recommended) - 1;
        if (page >= 0) {
            return page;
        }
        return 0;
    };

    window.addEventListener("resize", function () {
        screenWidth = window.innerWidth;
        if (screenWidth < 800) {
            particlesToShow = 1;
        } else if (screenWidth < 1200) {
            particlesToShow = 2;
        } else {
            particlesToShow = 3;
        }
    });
</script>

<section class="flex flex-col w-full h-full min-h-screen">
    {#if alternateLensCodes}
        <div
            class="w-full h-auto text-white shadow-inner bg-primary"
            on:click={() => switchUseType()}
        >
            <h3 class="py-3 mx-auto font-normal cursor-pointer w-max">
                {#if $state.contactLensUseType === ContactLensUseType.DAILY}
                    Click to see reusable alternatives &#8594
                {:else if $state.contactLensUseType === ContactLensUseType.REUSABLE}
                    Click to see daily alternatives &#8594
                {/if}
            </h3>
        </div>
    {/if}
    <div class="flex flex-col w-full max-w-full overflow-hidden md:flex-row">
        <div style="width:300px;" class="flex w-auto h-full">
            <div
                class="flex flex-col justify-center w-auto h-auto py-6 text-white contact-lens-sidebar-offset lg:justify-start bg-primary benefit-column"
            >
                {#each Object.entries(contactLensBenefits) as [key, benefit]}
                    <ContactLensBenefitColumn {benefit} mouseOverTarget=".benefit-{key}" />
                {/each}
            </div>
        </div>

        <div class="relative flex self-stretch flex-grow w-full h-auto overflow-hidden">
            <div class="flex flex-col w-full">
                <div class="contents carousel-wrapper">
                    {#key $state.contactLensUseType}
                        <Carousel
                            bind:this={carousel}
                            let:loaded
                            {particlesToShow}
                            infinite={false}
                            dots={false}
                            initialPageIndex={getInitialPage(lensCodes, recommended)}
                        >
                            {#each getLensDetails(contactLensOptions, lensCodes) as option, i}
                                <div
                                    id="lens-column"
                                    class="w-1/2 h-full px-4 py-8 flex md:min-h-full flex-col items-center
                          {isRecommended(lensCodes, recommended, i) ? 'recommended' : ''} "
                                    in:fly={{ y: 100, duration: 500, delay: i * 200 }}
                                >
                                    {#if isRecommended(lensCodes, recommended, i)}
                                        <span class="font-xl text center text-primary"
                                            >Recommended</span
                                        >
                                    {/if}
                                    <h1 class="text-4xl font-normal">{option.tier}</h1>
                                    <ContactLensCard
                                        name={getBaseLensName(option, $state.region)}
                                        image={getBaseLensImage(option, $state.region)}
                                        baseHeightClass={lensCardHeightClass}
                                    />
                                    {#if typeof option.name === "string"}
                                        <div class="w-full px-6 sm:h-52 md:h-14 lg:h-14">
                                            {option.description}
                                        </div>
                                    {/if}
                                    <div class="flex flex-col items-stretch w-full py-6">
                                        {#each Object.entries(contactLensBenefits) as [key, benefit]}
                                            {#if option.benefits.includes(benefit)}
                                                <div
                                                    class="h-14 m-auto text-4xl tick benefit-{key}"
                                                >
                                                    &#x2713
                                                </div>
                                            {:else}
                                                <div class="h-14" />
                                            {/if}
                                        {/each}
                                    </div>
                                    <table class="w-5/6 mt-auto text-lg table-auto">
                                        <thead>
                                            <tr>
                                                <th colspan="2" class="text-xl text-left"
                                                    >Monthly prices</th
                                                >
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {#each option.prices as row}
                                                <tr>
                                                    <td class="text-left">{row.description}</td>
                                                    <td class="font-bold text-right">
                                                        <span class="inline">
                                                            {getCurrency($state)}
                                                        </span>
                                                        {row.price[$state.region].toFixed(2)}
                                                    </td>
                                                </tr>
                                            {/each}
                                            <tr>
                                                <td class="font-bold text-right">Per box</td>
                                                <td class="font-bold text-right">
                                                    <span class="inline">
                                                        {getCurrency($state)}
                                                    </span>
                                                    {option.boxPrice[$state.region].toFixed(2)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <button
                                        class="px-4 mt-6 btn-primary btn-sm lg:px-8"
                                        type="button"
                                        on:click={() => push(detailURL(lensCodes, i))}
                                    >
                                        More details
                                    </button>
                                </div>
                            {/each}
                        </Carousel>
                    {/key}
                </div>
                <div class="flex flex-col h-auto py-8 text-3xl text-center">
                    {#if $state.contactLensUseType === ContactLensUseType.REUSABLE}
                        <p>All reusable lens prices include your cleaning solutions</p>
                    {/if}

                    <button
                        class="px-4 mx-auto mt-4 btn-secondary btn-sm lg:px-8"
                        type="button"
                        on:click={() => push("/")}
                    >
                        Start again
                    </button>
                </div>
            </div>
        </div>
        <div class="flex w-auto lg:justify-end">
            <img
                class="subscription self-center cursor-pointer relative"
                src="/img/contact-lens/subscription-banner.png"
                on:click={() => push("/contact-lenses/subscription/")}
            />
        </div>
    </div>
</section>

<style>
    :root {
        --sc-color-rgb-light-50p: #971c82 !important;
        --sc-color-hex-dark: #fff !important;
    }

    #lens-column:nth-child(2n) {
        @apply bg-primary-fade;
    }

    #lens-column.recommended {
        @apply pt-4;
    }

    #lens-column:not(.recommended) {
        @apply pt-10;
    }

    .local-container {
        @apply flex-row justify-center flex-wrap sm:flex-nowrap;
        @apply overflow-y-hidden;
    }

    .local-container > div.benefit-image {
        background-color: white;
    }

    .tick.highlighted {
        @apply text-primary text-5xl;
    }

    img.subscription {
        max-width: fit-content;
        top: -2em;
        width: 160px;
    }
</style>
